import { BinaryQuestionTemplate } from '@templates';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../routes/Routes';
import { useHighwaysStore } from '@highways/store/store';
import { useState } from 'react';
import {
  parseZodErrors,
  stringFieldSchema,
} from '@highways/utils/zod-validations';
import { RECEIVE_EMAIL_ERROR } from '@highways/constants/errorMessages';
import { ErrorMessages } from '@types';
import { ZodError } from 'zod';
import { useHandleSubmit } from '@highways/hooks/useHandleSubmit';
import { scrollTop } from '@utils';
import { basename } from '../routes/basename';

export const DoYouWantConfirmationEmail = () => {
  const navigate = useNavigate();
  const {
    updateWantConfirmationEmail,
    wantConfirmationEmail,
    getDataForSubmission,
    caseReferenceId,
  } = useHighwaysStore();
  const [result, setResult] = useState(wantConfirmationEmail);
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const [loading, setLoading] = useState(false);
  const pageTitle = 'Do you want a confirmation email of your report?';
  const { mutateAsync: submitForm, isPending } = useHandleSubmit();
  const handleChange = (result: string) => {
    setResult(result as 'Yes' | 'No');
    setErrorMessages([]);
  };

  const buttonNext = {
    label: result === 'No' ? 'Submit' : 'Next',
    loading: isPending || loading,
    onClick: async () => {
      setLoading(true);
      try {
        const validatedData = stringFieldSchema(
          'wantConfirmationEmail',
          RECEIVE_EMAIL_ERROR.required.message,
        ).parse({
          wantConfirmationEmail: result,
        });
        if (validatedData.wantConfirmationEmail) {
          updateWantConfirmationEmail(
            validatedData.wantConfirmationEmail as 'Yes' | 'No',
          );
          if (validatedData.wantConfirmationEmail === 'Yes') {
            navigate(RoutesList.YOUR_DETAILS);
          } else {
            if (caseReferenceId) {
              setErrorMessages([
                {
                  path: 'wantConfirmationEmail',
                  message:
                    'You have already submitted this report. Please return to the homepage.',
                },
              ]);
              return;
            }
            const data = getDataForSubmission();
            await submitForm(data);
            navigate(RoutesList.CONFIRMATION);
            // add this to enable popstate event listener to be triggered
            window.history.pushState(
              {},
              '',
              basename + RoutesList.CONFIRMATION,
            );
          }
          scrollTop();
        }
      } catch (error) {
        if (error instanceof ZodError) {
          const validationErrors = parseZodErrors(error as ZodError);
          setErrorMessages(
            validationErrors.map((error: ErrorMessages) => ({
              ...error,
              href: error.path,
            })),
          );
        } else {
          setErrorMessages([
            {
              message: (error as Error).message,
              path: 'wantConfirmationEmail',
            },
          ]);
        }
      } finally {
        setLoading(false);
      }
    },
  };

  const buttonPrevious = {
    disabled: isPending || loading,
    onClick: () => {
      navigate(RoutesList.ANYTHING_ELSE);
      scrollTop();
    },
  };

  const options = [
    {
      labelText: 'Yes',
      isDefault: wantConfirmationEmail === 'Yes',
    },
    {
      labelText: 'No',
      isDefault: wantConfirmationEmail === 'No',
    },
  ];

  return (
    <BinaryQuestionTemplate
      title={pageTitle}
      options={options}
      onChange={handleChange}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      errorPath={'wantConfirmationEmail'}
      errorMessages={errorMessages}
    />
  );
};
